import * as React from 'react';
import { Check, Lock } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single numbered section rendered by the accordion. */
export interface StepperAccordionSection {
  /** Body content rendered when this section is open (active). */
  content: React.ReactNode;
  /** Stable identifier — also the Radix accordion item value. */
  id: string;
  /** Recap/placeholder copy shown under the title while the section is closed (completed or locked). */
  summary?: React.ReactNode;
  /** Section title shown next to the badge. */
  title: React.ReactNode;
}

/** Translated aria text for the per-section badge, resolved by the caller (i18n-agnostic component). */
export interface StepperAccordionLabels {
  /** Accessible label for a completed section's badge (e.g. "Completed"). */
  completed: string;
  /** Accessible label for a locked section's badge (e.g. "Locked"). */
  locked: string;
  /** Accessible label for an active/upcoming section's badge, given its 1-based position and the total count. */
  stepPosition: (position: number, total: number) => string;
}

export interface StepperAccordionProps {
  /** The currently open section id, or `null` if none is open. */
  activeSectionId: string | null;
  /** Accessible name for the whole section list. */
  ariaLabel: string;
  /** Ids of sections already completed — their header becomes clickable to reopen. */
  completedSectionIds: string[];
  /** Optional additional CSS classes on the outer accordion. */
  className?: string;
  /** Translated aria labels for the section badges. */
  labels: StepperAccordionLabels;
  /** Fires only when the user clicks an *unlocked* section's header to reopen it. */
  onActiveSectionChange: (id: string) => void;
  /** Ordered sections — 1-based position is derived from array order. */
  sections: StepperAccordionSection[];
  /**
   * Ids of sections clickable even though not completed (e.g. a section visited then later
   * un-completed) — must be a superset of `completedSectionIds`. Defaults to `completedSectionIds`
   * for backward compatibility (only completed sections are clickable).
   */
  unlockedSectionIds?: string[];
}

type StepperAccordionSectionState = 'active' | 'completed' | 'locked' | 'unlocked';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Controlled, presentational numbered accordion: one section open at a time, locked future
 * sections, completed sections with a check badge that reopen on click. Fully i18n-agnostic —
 * all copy and accessible labels are supplied by the caller as props.
 */
export function StepperAccordion({
  activeSectionId,
  ariaLabel,
  className,
  completedSectionIds,
  labels,
  onActiveSectionChange,
  sections,
  unlockedSectionIds = completedSectionIds,
}: StepperAccordionProps): React.JSX.Element {
  function handleValueChange(value: string): void {
    // Radix reports an empty string when the user clicks the already-open trigger to collapse it —
    // ignored, since the open section stays open regardless (fully controlled from outside).
    if (!value || value === activeSectionId) return;
    if (unlockedSectionIds.includes(value)) {
      onActiveSectionChange(value);
    }
  }

  return (
    <Accordion
      aria-label={ariaLabel}
      className={cn('space-y-3', className)}
      onValueChange={handleValueChange}
      type="single"
      value={activeSectionId ?? ''}
    >
      {sections.map((section, index) => (
        <StepperAccordionItem
          key={section.id}
          labels={labels}
          position={index + 1}
          section={section}
          state={resolveSectionState(section.id, activeSectionId, completedSectionIds, unlockedSectionIds)}
          total={sections.length}
        />
      ))}
    </Accordion>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StepperAccordionItemProps {
  labels: StepperAccordionLabels;
  position: number;
  section: StepperAccordionSection;
  state: StepperAccordionSectionState;
  total: number;
}

function StepperAccordionItem({
  labels,
  position,
  section,
  state,
  total,
}: StepperAccordionItemProps): React.JSX.Element {
  const isActive = state === 'active';
  const isLocked = state === 'locked';

  return (
    <AccordionItem
      className={cn('overflow-hidden rounded-lg border bg-card shadow-sm', isActive && 'border-primary/30')}
      disabled={isLocked}
      value={section.id}
    >
      <AccordionTrigger className={cn('px-4 hover:no-underline', isLocked && '[&>svg]:hidden')}>
        <div className="flex flex-1 items-center gap-3">
          <StepperAccordionBadge labels={labels} position={position} state={state} total={total} />
          <div className="flex flex-col items-start text-start">
            <span
              className={cn(
                'font-medium',
                isActive && 'text-primary',
                isLocked ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {section.title}
            </span>
            {!isActive && section.summary !== undefined && (
              <span className="text-sm text-muted-foreground">{section.summary}</span>
            )}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="border-t border-border px-3 pb-4 pt-4 sm:px-4">{section.content}</AccordionContent>
    </AccordionItem>
  );
}

interface StepperAccordionBadgeProps {
  labels: StepperAccordionLabels;
  position: number;
  state: StepperAccordionSectionState;
  total: number;
}

function StepperAccordionBadge({ labels, position, state, total }: StepperAccordionBadgeProps): React.JSX.Element {
  const ariaLabel = resolveBadgeAriaLabel(labels, state, position, total);

  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        state === 'active' && 'bg-primary text-primary-foreground ring-2 ring-primary/15',
        state === 'completed' && 'bg-primary text-primary-foreground',
        state === 'locked' && 'bg-muted text-muted-foreground',
        state === 'unlocked' && 'border border-border bg-background text-foreground',
      )}
    >
      <span className="sr-only">{ariaLabel}</span>
      {resolveBadgeIcon(state, position)}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveSectionState(
  id: string,
  activeSectionId: string | null,
  completedSectionIds: string[],
  unlockedSectionIds: string[],
): StepperAccordionSectionState {
  if (id === activeSectionId) return 'active';
  if (completedSectionIds.includes(id)) return 'completed';
  if (unlockedSectionIds.includes(id)) return 'unlocked';
  return 'locked';
}

function resolveBadgeAriaLabel(
  labels: StepperAccordionLabels,
  state: StepperAccordionSectionState,
  position: number,
  total: number,
): string {
  if (state === 'completed') return labels.completed;
  if (state === 'locked') return labels.locked;
  // 'active' and 'unlocked' both show a numbered badge — reuse the same step-position label.
  return labels.stepPosition(position, total);
}

function resolveBadgeIcon(state: StepperAccordionSectionState, position: number): React.ReactNode {
  if (state === 'completed') return <Check aria-hidden="true" className="h-3.5 w-3.5" />;
  if (state === 'locked') return <Lock aria-hidden="true" className="h-3.5 w-3.5" />;
  return <span aria-hidden="true">{position}</span>;
}
